import { useEffect, useState } from "react";
import { Alert, Stack, Typography } from "@mui/material";
import { listProjectTmy } from "../../services/api";
import { normalizeSimulationYear } from "../../utils/simulationYears";
import PvLoadingRegion from "../pv/PvLoadingRegion";

// Read the same per-asset activation flags as Weather Data, independently of
// the weather recorded in a previous generation result.
export default function SelectedWeatherSummary({
  projectId,
  accessToken,
  simulationYear,
  onSelection = null,
  isGreek = false,
}) {
  const [selection, setSelection] = useState(null);

  useEffect(() => {
    let alive = true;
    setSelection(null);
    if (!projectId) return;
    listProjectTmy(projectId, accessToken)
      .then((data) => {
        if (alive) setSelection({ projectId, accessToken, active: (data?.imports || []).find((row) => row.is_active_pv) });
      })
      .catch(() => {
        if (alive) setSelection({ projectId, accessToken, error: true });
      });
    return () => { alive = false; };
  }, [projectId, accessToken]);

  const current = selection?.projectId === projectId && selection?.accessToken === accessToken ? selection : null;
  const active = current?.active;

  // Reported upward so the page can tell whether a stored run used this same
  // dataset. Nothing is reported while the selection is still loading or failed
  // to load: "unknown" must not be mistaken for "none selected".
  useEffect(() => {
    if (!onSelection) return;
    if (!current || current.error) {
      onSelection(null);
      return;
    }
    onSelection({ datasetId: active?.id ?? null, filename: active?.original_filename || null });
  }, [onSelection, current, active]);
  const source = active
    ? active.origin === "fetch" ? "External provider" : "Imported TMY"
    : "Platform internal";

  return (
    <Stack spacing={0.5}>
      {!projectId ? (
        <Typography variant="body2" color="text.secondary">Save the project to view the selected weather source.</Typography>
      ) : !current ? (
        <PvLoadingRegion minHeight={80} isGreek={isGreek} />
      ) : current.error ? (
        <Alert severity="warning">Unable to load the selected weather source.</Alert>
      ) : (
        <>
          <Typography variant="body2">
            <strong>Selected weather source:</strong> <span>{source}</span>
          </Typography>
          <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
            <strong>File:</strong>{" "}
            <span translate="no">{active ? active.original_filename || active.id : "—"}</span>
          </Typography>
          {active ? (
            <Typography variant="caption" color="text.secondary">
              <span>Weather data comes from the selected file.</span>
            </Typography>
          ) : (
            <Typography variant="body2">
              <strong>Weather data year:</strong> {normalizeSimulationYear(simulationYear)}
            </Typography>
          )}
        </>
      )}
    </Stack>
  );
}
